<template>
  <div class="music-page white-theme">
    <div class="container">
      <!-- Breadcrumb -->
      <div class="breadcrumb">
        <router-link to="/">{{ $t('music.breadcrumb.home') }}</router-link>
        <span>&rsaquo;</span>
        <span>{{ $t('music.breadcrumb.aiMusic') }}</span>
      </div>

      <!-- Main Generator Layout（与 Video / Image 一致：左表单 + 右历史） -->
      <div class="gen-layout-new">
        <!-- Left: Generator Form -->
        <div class="gen-form-col">
          <div class="gen-form-scroll">
            <!-- Model Selector Card -->
            <div class="model-selector-card">
              <div class="model-selector-label">{{ $t('music.model.label') }}</div>
              <div class="model-selector-body">
                <h2 class="model-name">{{ $t('music.model.aiMusicName') }}</h2>
                <div class="model-tags">
                  <span class="model-tag">{{ $t('music.model.tagMusic') }}</span>
                  <span class="model-tag">{{ $t('music.model.tagLyrics') }}</span>
                </div>
              </div>
            </div>

            <!-- Lyrics Form -->
            <div class="gen-form">
              <div class="form-group">
                <label class="form-label">{{ $t('music.form.songTitle') }} *</label>
                <input type="text" v-model="songTitle" class="text-input" :placeholder="$t('music.form.songTitlePlaceholder')">
              </div>
              <div class="form-group prompt-group">
                <label class="form-label">{{ $t('music.form.lyricsDescLabel') }} *</label>
                <div class="quick-tags between-label-input">
                  <button
                    v-for="tag in sectionTags"
                    :key="tag.value"
                    type="button"
                    class="quick-tag"
                    @click="insertSectionTag(tag)">
                    {{ tag.label }}
                  </button>
                </div>
                <textarea ref="lyricsTextarea" v-model="lyrics" class="prompt-input lyrics-input" :placeholder="$t('music.form.lyricsDescPlaceholder')" rows="10"></textarea>
                <div class="prompt-footer prompt-footer-right">
                  <div class="char-count">{{ lyrics.length }}/3000</div>
                </div>
              </div>
              <div class="form-group prompt-group">
                <label class="form-label">{{ $t('music.form.styleTags') }}</label>
                <textarea v-model="styleTags" class="prompt-input" :placeholder="$t('music.form.styleTagsPlaceholder')" rows="2" style="padding-bottom:14px;"></textarea>
                <div class="tag-list">
                  <button
                    v-for="tag in styleTagOptions"
                    :key="tag.value"
                    type="button"
                    :class="['tag-chip', { active: isStyleSelected(tag.value) }]"
                    @click="toggleStyleTag(tag.value)">
                    {{ tag.label }}
                  </button>
                </div>
              </div>
            </div>

            <div class="switch-row switch-row-in-form">
              <span>{{ $t('music.options.instrumental') }}</span>
              <span class="switch-desc"></span>
              <label class="switch">
                <input type="checkbox" v-model="instrumental">
                <span class="switch-slider"></span>
              </label>
            </div>
          </div>

          <!-- 与 VideoGeneratorNew 一致：单行底栏，固定于左栏底部 -->
          <div class="gen-actions gen-actions-fixed">
            <div class="gen-credits-info">
              <span class="credits-num">{{ userCredits }}</span> {{ $t('modelDetail.creditsRequired') }}
              <span :class="insufficientCredits ? 'credits-insufficient' : 'credits-required'">{{ requiredCredits }}</span>
            </div>
            <button
              v-if="!insufficientCredits"
              class="gen-btn-generate"
              :disabled="generating"
              @click="generate"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              <span>{{ generating ? ($t('home.generatorNew.generating') || 'Generating...') : $t('home.generate') }}</span>
            </button>
            <router-link
              v-else
              class="gen-btn-credits"
              :to="pricingPath"
            >{{ $t('modelDetail.getMoreCredits') }}</router-link>
          </div>
          <div v-if="formError" class="form-error">{{ formError }}</div>
        </div>

        <!-- Right: 历史（与 VideoGeneratorNew / ImageGeneratorNew 一致） -->
        <div class="gen-history-col">
          <div class="gen-history-header">
            <span>{{ $t('home.generatorNew.historyTitle') }}</span>
            <router-link class="gen-history-assets-btn" :to="assetsPath">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              <span>{{ $t('home.generatorNew.myAssets') }}</span>
            </router-link>
          </div>
          <GenerationHistory
            :items="historyItems"
            type="music"
            :generating="generating"
          />
        </div>
      </div>

      <!-- Introduction -->
      <div class="intro-section">
        <h1 class="intro-title">{{ $t('music.intro.title') }}</h1>
        <p class="intro-desc">{{ $t('music.intro.desc') }}</p>
        <div class="intro-tags">
          <span class="intro-tag">{{ $t('music.intro.tagMusic') }}</span>
          <span class="intro-tag">{{ $t('music.intro.tagLyrics') }}</span>
          <span class="intro-tag">{{ $t('music.intro.tagStyles') }}</span>
          <span class="intro-tag">{{ $t('music.intro.tagCommercial') }}</span>
        </div>
      </div>

      <!-- Features -->
      <div class="features-section">
        <span class="section-badge">{{ $t('music.features.badge') }}</span>
        <h2 class="section-heading">{{ $t('music.features.title') }}</h2>
        <div class="features-grid">
          <div class="feature-card" v-for="feat in features" :key="feat.titleKey">
            <div class="feature-icon">{{ feat.icon }}</div>
            <h3>{{ $t(feat.titleKey) }}</h3>
            <p>{{ $t(feat.descKey) }}</p>
          </div>
        </div>
      </div>

      <!-- FAQ -->
      <div class="faq-section">
        <h2 class="section-heading">{{ $t('music.faq.title') }}</h2>
        <div class="faq-list">
          <details v-for="(faq, idx) in faqs" :key="idx" class="faq-item">
            <summary>{{ $t(faq.qKey) }}</summary>
            <p>{{ $t(faq.aKey) }}</p>
          </details>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { generateContent, pollGenerationStatus, saveOrUpdateAIHistory, formatCreateDate } from '@/api'
import { saveMediaBlobList, saveMediaCoverBlobList } from '@/utils/mediaCache'
import { HISTORY_SOURCE_ALLALL, HISTORY_SOURCE_SUNO } from '@/config/ai-history-source'
import GenerationHistory from '@/components/common/GenerationHistory.vue'
var generationHistoryMixin = require('../mixins/generationHistoryMixin')

export default {
  name: 'Music',
  mixins: [generationHistoryMixin],
  components: {
    GenerationHistory: GenerationHistory
  },
  data: function() {
    return {
      lyrics: '',
      songTitle: '',
      styleTags: '',
      instrumental: false,
      generating: false,
      poller: null,
      selectedStyleValues: [],
      formError: null,
      userCredits: 0,
      mixinType: 'music',
      mixinDefaultAiName: 'AI Music',
      paramValues: { prompt: '' },
      // 生成按钮点击前，tracks 数量在接口返回前未知；这里按默认每次返回 2 个音轨做预估扣分
      expectedMusicTracksCount: 2,
      tracks: [],
      features: [
        { icon: '🎵', titleKey: 'music.features.items.styles.title', descKey: 'music.features.items.styles.desc' },
        { icon: '📝', titleKey: 'music.features.items.lyrics.title', descKey: 'music.features.items.lyrics.desc' },
        { icon: '🎤', titleKey: 'music.features.items.vocals.title', descKey: 'music.features.items.vocals.desc' },
        { icon: '🎹', titleKey: 'music.features.items.instrumental.title', descKey: 'music.features.items.instrumental.desc' },
        { icon: '⚡', titleKey: 'music.features.items.fast.title', descKey: 'music.features.items.fast.desc' },
        { icon: '💼', titleKey: 'music.features.items.commercial.title', descKey: 'music.features.items.commercial.desc' }
      ],
      faqs: [
        { qKey: 'music.faq.items.howItWorks.q', aKey: 'music.faq.items.howItWorks.a' },
        { qKey: 'music.faq.items.commercial.q', aKey: 'music.faq.items.commercial.a' },
        { qKey: 'music.faq.items.styles.q', aKey: 'music.faq.items.styles.a' },
        { qKey: 'music.faq.items.credits.q', aKey: 'music.faq.items.credits.a' },
        { qKey: 'music.faq.items.download.q', aKey: 'music.faq.items.download.a' }
      ]
    }
  },
  computed: {
    requiredCredits: function() {
      var trackCount = (this.tracks && this.tracks.length) ? this.tracks.length : this.expectedMusicTracksCount
      return 10 * (trackCount || 0)
    },
    insufficientCredits: function() {
      return (Number(this.userCredits) || 0) < this.requiredCredits
    },
    pricingPath: function() {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/pricing' : '/pricing'
    },
    assetsPath: function() {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + '/gallery' : '/gallery'
    },
    model: function() {
      return { name: this.mixinDefaultAiName || 'AI Music' }
    },
    styleTagOptions: function() {
      var raw = this.$t('music.tags.styles')
      if (Array.isArray(raw)) return raw
      return []
    },
    sectionTags: function() {
      var raw = this.$t('music.tags.sections')
      if (Array.isArray(raw)) return raw
      return []
    },
    selectedStyleLabels: function() {
      var self = this
      return (this.selectedStyleValues || []).map(function(val) {
        var hit = self.styleTagOptions.find(function(t) { return t && t.value === val })
        return hit ? hit.label : val
      }).filter(Boolean)
    },
  },
  mounted: function() {
    var u = typeof localStorage !== 'undefined' && localStorage.getItem('userInfo')
    if (u) {
      try {
        var o = JSON.parse(u)
        this.userCredits = o.credits != null ? o.credits : 0
      } catch (e) {}
    }
    var q = this.$route && this.$route.query ? this.$route.query : {}
    var lyricsFromQuery = (q.lyrics || q.prompt || '').toString().trim()
    if (lyricsFromQuery) {
      this.lyrics = lyricsFromQuery
    }
    var titleFromQuery = (q.songTitle || q.title || '').toString().trim()
    if (titleFromQuery) {
      this.songTitle = titleFromQuery
    }
    var tagsFromQuery = (q.styleTags || q.tags || '').toString().trim()
    if (tagsFromQuery) {
      this.styleTags = tagsFromQuery
    }
    if (typeof this._fetchHistory === 'function') {
      this._fetchHistory()
    }
  },
  methods: {
    isStyleSelected: function(value) {
      return this.selectedStyleValues.indexOf(value) !== -1
    },
    syncStyleTagsInput: function() {
      var self = this
      var labels = this.selectedStyleValues.map(function(val) {
        var hit = self.styleTagOptions.find(function(t) { return t && t.value === val })
        return hit ? hit.label : val
      }).filter(Boolean)
      this.styleTags = labels.join(', ')
    },
    toggleStyleTag: function(value) {
      var idx = this.selectedStyleValues.indexOf(value)
      if (idx === -1) this.selectedStyleValues.push(value)
      else this.selectedStyleValues.splice(idx, 1)
      this.syncStyleTagsInput()
    },
    insertSectionTag: function(tag) {
      var token = (tag && tag.value) ? tag.value : ''
      if (!token) return
      var el = this.$refs.lyricsTextarea
      if (!el) {
        this.lyrics = (this.lyrics || '') + token
        return
      }
      var start = typeof el.selectionStart === 'number' ? el.selectionStart : (this.lyrics || '').length
      var end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start
      var before = (this.lyrics || '').slice(0, start)
      var after = (this.lyrics || '').slice(end)
      this.lyrics = before + token + after
      var nextPos = start + token.length
      this.$nextTick(function() {
        try {
          el.focus()
          el.setSelectionRange(nextPos, nextPos)
        } catch (e) {}
      })
    },
    /**
     * 音乐任务失败收尾：只做第二次失败上报（state:3）。
     * 第一次 state:1 已在点击生成时完成。
     */
    reportMusicSubmitRequestFailed: function(createDate, aiDesc, historyId, taskId, aiSource) {
      var aiName = 'AI Music'
      var desc = (aiDesc || '').toString().slice(0, 2000)
      return saveOrUpdateAIHistory({
        aiName: aiName,
        historyId: historyId,
        taskId: taskId || '',
        aiDesc: desc,
        state: 3,
        message: (typeof error !== 'undefined' && error && error.message) ? error.message : ((typeof err !== 'undefined' && err && err.message) ? err.message : ((typeof statusRes !== 'undefined' && statusRes && statusRes.error) ? statusRes.error : '')),
        aiResultUrl: '',
        createDate: createDate,
        aiSource: aiSource
      }).catch(function() {})
    },
    generate: function() {
      this.formError = null
      if (this.generating) return
      if (!this.songTitle.trim()) {
        this.formError = this.$t('music.errors.titleRequired')
        return
      }
      if (!this.lyrics.trim()) {
        this.formError = this.$t('music.errors.lyricsDescRequired')
        return
      }
      if (this.insufficientCredits) {
        this.$router.push('/pricing')
        return
      }
      this.revokeTrackObjectUrls()
      this.paramValues.prompt = (this.lyrics || '').trim()
      this.generating = true
      this.tracks = []
      this.onGenerationStart()
      var self = this
      var createDate = formatCreateDate(new Date())
      var aiName = 'AI Music'
      var aiDesc = (this.lyrics || '').trim().slice(0, 2000)
      var historyId = null
      var aiSource = HISTORY_SOURCE_SUNO
      var params = {
        modelId: 'suno-music',
        type: 'music',
        prompt: this.lyrics,
        title: this.songTitle,
        tags: this.styleTags,
        make_instrumental: this.instrumental
      }
      var isOfficialChannel = false
      var startHistoryPromise = saveOrUpdateAIHistory({
        aiName: aiName,
        taskId: '',
        aiDesc: aiDesc,
        state: 1,
        aiResultUrl: '',
        createDate: createDate,
        aiSource: aiSource
      }).then(function(hRes) {
        historyId = hRes && (hRes.historyId != null ? hRes.historyId : null)
      })

      startHistoryPromise.then(function() {
        return generateContent(params)
      }).then(function(res) {
        var wanUnifiedMusic = res.provider === 'wan-unified'
        isOfficialChannel = res.provider === 'official'
        var taskId = res.taskId
        aiSource = res.provider === 'allall' ? HISTORY_SOURCE_ALLALL : HISTORY_SOURCE_SUNO
        if (!taskId) {
          self.generating = false
          self.formError = self.$t('common.generateFailedContactSupport')
          self.onGenerationFailed({ message: self.formError })
          self.reportMusicSubmitRequestFailed(createDate, aiDesc, historyId, '', aiSource)
          return
        }
        saveOrUpdateAIHistory({
          aiName: aiName,
          historyId: historyId,
          taskId: taskId,
          aiDesc: aiDesc,
          state: 1,
          aiResultUrl: '',
          createDate: createDate,
          aiSource: aiSource
        }).then(function(hRes) {
          historyId = hRes && (hRes.historyId != null ? hRes.historyId : historyId)
        }).catch(function() {})
        var pollOptions = res.provider ? {
          provider: res.provider,
          allallApi: res.allallApi,
          officialProfile: res.officialProfile,
          officialMedia: res.officialMedia,
          wanCreateId: res.createId,
          wanModelName: res.wanModelName,
          wanModelVersion: res.wanModelVersion,
          wanModelType: res.wanModelType
        } : undefined
        self.poller = pollGenerationStatus(taskId, function(err, statusRes) {
          if (err) {
            self.generating = false
            self.formError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
            self.onGenerationFailed({ message: self.formError })
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: taskId,
              aiDesc: aiDesc,
              state: 3,
              message: (typeof error !== 'undefined' && error && error.message) ? error.message : ((typeof err !== 'undefined' && err && err.message) ? err.message : ((typeof statusRes !== 'undefined' && statusRes && statusRes.error) ? statusRes.error : '')),
              aiResultUrl: '',
              createDate: createDate,
              aiSource: aiSource
            }).catch(function() {})
            return
          }
          if (statusRes.status === 'completed' && statusRes.tracks && statusRes.tracks.length) {
            var musicTracks = statusRes.tracks || []
            var blobs = Array.isArray(statusRes.audioBlobs) ? statusRes.audioBlobs : []
            var coverBlobs = Array.isArray(statusRes.coverBlobs) ? statusRes.coverBlobs : []
            var audioOk =
              blobs.length === musicTracks.length &&
              blobs.every(function (b) {
                return b && b.size
              })
            var coverOk =
              coverBlobs.length === musicTracks.length &&
              coverBlobs.every(function (b) {
                return b && b.size
              })
            var allBlobsOk = (isOfficialChannel || wanUnifiedMusic) && audioOk
            var credits = 10 * musicTracks.length
            var aiResultUrl = musicTracks
              .map(function(item) { return item && item.audio_url })
              .filter(Boolean)
              .join(',')
            function sunoApplyCredits(res) {
              if (res && res.totalCredits != null) {
                var u = typeof localStorage !== 'undefined' && localStorage.getItem('userInfo')
                if (u) {
                  try {
                    var o = JSON.parse(u)
                    o.credits = res.totalCredits
                    localStorage.setItem('userInfo', JSON.stringify(o))
                    self.userCredits = res.totalCredits
                  } catch (e) {}
                }
              }
            }
            function sunoFinishSuccess(aiResUrl) {
              self.tracks = musicTracks.map(function (item, idx) {
                var remoteAudio = item.audio_url || ''
                var audioUrl = remoteAudio
                if (audioOk && blobs[idx] && blobs[idx].size && (isOfficialChannel || wanUnifiedMusic)) {
                  audioUrl = URL.createObjectURL(blobs[idx])
                }
                var remoteCover = item.image_large_url || item.image_url || ''
                var coverUrl = remoteCover
                if (coverBlobs[idx] && coverBlobs[idx].size) {
                  coverUrl = URL.createObjectURL(coverBlobs[idx])
                }
                return {
                  coverUrl: coverUrl,
                  audioUrl: audioUrl,
                  title: item.title || '',
                  lyrics: item.prompt || '',
                  tags: item.tags || '',
                  duration: Number(item.duration) || 0
                }
              })
              self.generating = false
              var audioUrls = self.tracks.map(function (t) { return t && t.audioUrl }).filter(Boolean)
              self.onGenerationComplete({ urls: audioUrls })
              saveOrUpdateAIHistory({
                aiName: aiName,
                historyId: historyId,
                taskId: taskId,
                aiDesc: aiDesc,
                state: 2,
                aiResultUrl: aiResUrl,
                image_large_url: '',
                createDate: createDate,
                credits: credits,
                aiSource: aiSource
              })
                .then(sunoApplyCredits)
                .catch(function() {})
            }
            if (allBlobsOk) {
              var saveMusic = []
              if (historyId) saveMusic.push(saveMediaBlobList(historyId, blobs))
              if (taskId) saveMusic.push(saveMediaBlobList(taskId, blobs))
              if (wanUnifiedMusic && coverOk) {
                if (historyId) saveMusic.push(saveMediaCoverBlobList(historyId, coverBlobs))
                if (taskId) saveMusic.push(saveMediaCoverBlobList(taskId, coverBlobs))
              }
              Promise.all(saveMusic)
                .then(function () {
                  sunoFinishSuccess('')
                })
                .catch(function (e) {
                  console.warn('[Suno] saveMediaBlobList failed', e)
                  var fmSuno = self.$t('common.localMediaTransferFailed')
                  self.generating = false
                  self.tracks = []
                  self.formError = fmSuno
                  self.onGenerationFailed({ message: fmSuno })
                  saveOrUpdateAIHistory({
                    aiName: aiName,
                    historyId: historyId,
                    taskId: taskId,
                    aiDesc: aiDesc,
                    state: 3,
                    message: fmSuno,
                    aiResultUrl: '',
                    createDate: createDate,
                    aiSource: aiSource
                  }).catch(function() {})
                })
              return
            }
            sunoFinishSuccess(aiResultUrl)
          } else if (statusRes.status === 'failed') {
            self.generating = false
            self.formError = self.$userFacingError(statusRes, self.$t('common.generateFailedContactSupport'))
            self.onGenerationFailed({ message: self.formError })
            saveOrUpdateAIHistory({
              aiName: aiName,
              historyId: historyId,
              taskId: taskId,
              aiDesc: aiDesc,
              state: 3,
              message: (typeof error !== 'undefined' && error && error.message) ? error.message : ((typeof err !== 'undefined' && err && err.message) ? err.message : ((typeof statusRes !== 'undefined' && statusRes && statusRes.error) ? statusRes.error : '')),
              aiResultUrl: '',
              createDate: createDate,
              aiSource: aiSource
            }).catch(function() {})
          }
        }, 4000, pollOptions || { provider: 'allall', allallApi: 'suno/music' })
      }).catch(function(err) {
        self.generating = false
        self.formError = self.$userFacingError(err, self.$t('common.generateFailedContactSupport'))
        self.onGenerationFailed({ message: self.formError })
        self.reportMusicSubmitRequestFailed(createDate, aiDesc, historyId, '', aiSource)
      })
    },
    revokeTrackObjectUrls: function() {
      if (!this.tracks || !this.tracks.length) return
      this.tracks.forEach(function (t) {
        var u = t && t.audioUrl
        if (u && String(u).indexOf('blob:') === 0) {
          try {
            URL.revokeObjectURL(u)
          } catch (e) {}
        }
      })
    }
  },
  beforeDestroy: function() {
    if (this.poller && typeof this.poller.stop === 'function') this.poller.stop()
    this.revokeTrackObjectUrls()
  }
}
</script>

<style scoped>
@import './GeneratorNewStyles.css';

.music-page { padding: 24px 0 80px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; margin-bottom: 24px; font-size: 13px; color: var(--text-muted); }
.breadcrumb a { color: var(--text-secondary); }
.breadcrumb a:hover { color: var(--accent); }

/* 与 Video / Image 一致的两栏（左表单 + 右历史） */
.music-page .gen-layout-new {
  margin-bottom: 48px;
  align-items: start;
}

/* Left Column：中间滚动，底栏固定（同 VideoGeneratorNew） */
.music-page .gen-form-col {
  background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  border-radius: var(--radius-xl);
  padding: 24px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 180px);
  position: sticky;
  top: 80px;
  overflow: hidden !important;
  overflow-y: hidden !important;
  min-height: 0;
}
.music-page .gen-form-scroll {
  flex: 1 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 16px !important;
  -webkit-overflow-scrolling: touch;
}
.gen-form-scroll::-webkit-scrollbar { width: 6px; }
.gen-form-scroll::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 3px; }
.gen-form-scroll::-webkit-scrollbar-track { background: transparent; }

.music-page .gen-actions {
  border-top: none !important;
}
.music-page .gen-actions.gen-actions-fixed {
  flex-shrink: 0 !important;
  margin-top: 12px !important;
  border-top: 1px solid #e5e7eb !important;
  background: #fff !important;
  position: sticky;
  bottom: 0;
  z-index: 2;
}
.music-page .gen-credits-info {
  min-width: 0;
}

/* Model Selector */
.model-selector-card { display: flex; align-items: center; gap: 16px; padding: 16px; border: 1px solid var(--border-color); border-radius: var(--radius-lg); cursor: default; transition: all 0.2s; }
.model-selector-card:hover { border-color: #444; }
.model-selector-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; writing-mode: vertical-lr; letter-spacing: 0.1em; }
.model-selector-body { flex: 1; }
.model-name { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
.model-tags { display: flex; gap: 8px; }
.model-tag { padding: 4px 12px; border-radius: var(--radius-full); border: 1px solid var(--accent-border); background: var(--accent-light); color: var(--accent); font-size: 12px; }

/* Form */
.gen-form { display: block; }
.form-group { margin-bottom: 16px; }
.form-label { font-size: 13px; font-weight: 600; margin-bottom: 8px; display: block; }
.text-input { width: 100%; padding: 12px 14px; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: var(--radius-md); color: var(--text-primary); font-size: 14px; font-family: inherit; outline: none; }
.text-input:focus { border-color: var(--accent); }

/* Prompt */
.prompt-group { position: relative; }
.prompt-input { width: 100%; padding: 14px; padding-bottom: 36px; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: var(--radius-md); color: var(--text-primary); font-size: 14px; font-family: inherit; resize: vertical; outline: none; }
.prompt-input:focus { border-color: var(--accent); }
.lyrics-input { font-family: 'Courier New', monospace; line-height: 1.6; }
.prompt-footer { margin-top: 6px; display: flex; }
.prompt-footer-right { justify-content: flex-end; }
.char-count { font-size: 11px; color: var(--text-muted); }

/* Section tags between label and textarea */
.quick-tags { display: flex; gap: 6px; flex-wrap: wrap; }
.quick-tags.between-label-input { margin-bottom: 10px; }
.quick-tag { padding: 4px 10px; border-radius: var(--radius-full); border: 1px solid var(--border-color); color: var(--text-secondary); background: var(--bg-primary); cursor: pointer; font-size: 12px; transition: all 0.15s; }
.quick-tag:hover { border-color: #444; }

.tag-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.tag-chip { padding: 4px 10px; border-radius: var(--radius-full); border: 1px solid var(--border-color); background: var(--bg-primary); color: var(--text-secondary); font-size: 12px; cursor: pointer; transition: all 0.15s; }
.tag-chip:hover { border-color: #444; }
.tag-chip.active { border-color: var(--accent); background: var(--accent-light); color: var(--accent); }

/* Switch */
.switch-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; font-size: 14px; color: var(--text-secondary); }
.switch-desc { flex: 1; font-size: 12px; color: var(--text-muted); }
.switch { position: relative; display: inline-block; width: 44px; height: 24px; flex-shrink: 0; }
.switch input { opacity: 0; width: 0; height: 0; }
.switch-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: var(--border-color); border-radius: 24px; transition: 0.3s; }
.switch-slider::before { content: ''; position: absolute; height: 18px; width: 18px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: 0.3s; }
input:checked + .switch-slider { background: var(--accent); }
input:checked + .switch-slider::before { transform: translateX(20px); }

.switch-row-in-form {
  margin-top: 4px;
  padding-bottom: 4px;
  border-top: 1px solid var(--border-color);
  padding-top: 14px;
}
.form-error { margin-top: 10px; color: #ff6b6b; font-size: 13px; }

.music-page .gen-history-col {
  background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  border-radius: var(--radius-xl);
  min-height: 400px;
}

/* Intro Section */
.intro-section { text-align: center; padding: 48px 0; max-width: 800px; margin: 0 auto; }
.intro-title { font-size: 36px; font-weight: 800; margin-bottom: 16px; }
.intro-desc { font-size: 15px; color: var(--text-secondary); line-height: 1.7; margin-bottom: 24px; }
.intro-tags { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
.intro-tag { padding: 6px 16px; border-radius: var(--radius-full); border: 1px solid var(--accent-border); background: var(--accent-light); color: var(--accent); font-size: 13px; }

/* Features */
.features-section { padding: 48px 0; }
.section-badge { display: inline-flex; padding: 6px 16px; border-radius: var(--radius-full); border: 1px solid var(--accent-border); background: var(--accent-light); color: var(--accent); font-size: 13px; margin-bottom: 16px; }
.section-heading { font-size: 28px; font-weight: 700; margin-bottom: 32px; }
.features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.feature-card { background: var(--bg-card); border: 1px solid var(--bg-card-border); border-radius: var(--radius-lg); padding: 24px; }
.feature-icon { font-size: 28px; margin-bottom: 12px; }
.feature-card h4 { font-size: 16px; font-weight: 600; margin-bottom: 8px; }
.feature-card p { font-size: 13px; color: var(--text-secondary); line-height: 1.6; }

/* FAQ */
.faq-section { padding: 48px 0; }
.faq-list { max-width: 700px; }
.faq-item { border: 1px solid var(--border-color); border-radius: var(--radius-md); margin-bottom: 8px; overflow: hidden; }
.faq-item summary { padding: 16px 20px; font-size: 15px; font-weight: 600; cursor: pointer; list-style: none; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { content: ' ▼'; font-size: 10px; color: var(--text-muted); }
.faq-item[open] summary::after { content: ' ▲'; }
.faq-item p { padding: 0 20px 16px; font-size: 14px; color: var(--text-secondary); line-height: 1.7; }

@media (max-width: 1024px) {
  .music-page .gen-layout-new { grid-template-columns: 1fr; }
  .features-grid { grid-template-columns: 1fr; }
}
</style>
